import {
	ActionBar,
	DatePicker,
	Select,
	SpinnerWithBackground,
	Text,
	UnsavedChangesDialog,
	useUnsavedChangesWarning,
} from '@viapeople/components'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useEnpsStore } from '../store'
import type { EnpsScheduleTabInput } from './types'

/**
 * @fileoverview eNPS Schedule Tab
 *
 * Configure survey cadence and scheduling
 */

const cadenceOptions = [
	{ value: 'one-time', label: 'One-time' },
	{ value: 'weekly', label: 'Weekly' },
	{ value: 'biweekly', label: 'Bi-weekly' },
	{ value: 'monthly', label: 'Monthly' },
	{ value: 'quarterly', label: 'Quarterly' },
	{ value: 'annually', label: 'Annually' },
]

export const EnpsScheduleTab = () => {
	const { organizationId: _organizationId } = useParams<{
		organizationId: string
		enpsId: string
	}>()
	const navigate = useNavigate()
	const { enpsConfig, useEditEnpsConfig } = useEnpsStore()
	const { editEnpsConfig, isLoading } = useEditEnpsConfig(enpsConfig?.id)

	const {
		handleSubmit,
		control,
		formState: { errors, isDirty },
		reset,
	} = useForm<EnpsScheduleTabInput>({
		defaultValues: {
			cadence: enpsConfig?.cadence || 'quarterly',
			nextScheduledDate: enpsConfig?.nextScheduledDate || null,
		},
	})

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	useEffect(() => {
		if (enpsConfig) {
			reset({
				cadence: enpsConfig.cadence,
				nextScheduledDate: enpsConfig.nextScheduledDate,
			})
		}
	}, [enpsConfig, reset])

	const handleSubmitInternal = async (data: EnpsScheduleTabInput) => {
		await editEnpsConfig(data)
		reset(data)
	}

	return (
		<form onSubmit={handleSubmit(handleSubmitInternal)} className='relative pb-12 mt-6 w-full'>
			{isLoading && <SpinnerWithBackground />}
			<div className='grid grid-cols-1 md:grid-cols-2 gap-x-6 xl:gap-x-12 gap-6 px-6 items-start max-w-4xl'>
				<div>
					<Controller
						name='cadence'
						control={control}
						rules={{ required: 'Cadence is required' }}
						render={({ field: { value, onChange } }) => (
							<div className='flex flex-col'>
								<div className='flex flex-col'>
									<Text variant='small' weight='semibold' className='mb-2'>
										Survey Cadence <span className='text-red-500'>*</span>
									</Text>
									<Select
										placeholder='Select frequency'
										variant={errors.cadence ? 'error' : 'default'}
										value={value}
										onChange={onChange}
										options={cadenceOptions}
									/>
								</div>
								{errors.cadence && (
									<Text variant='xsmall' className='text-red-500 mt-1'>
										{errors.cadence.message}
									</Text>
								)}
								<Text variant='xsmall' className='text-gray-600 mt-2'>
									How often should this survey be sent to participants
								</Text>
							</div>
						)}
					/>
				</div>

				<div>
					<Controller
						name='nextScheduledDate'
						control={control}
						render={({ field: { value, onChange } }) => (
							<div className='flex flex-col'>
								<Text variant='small' weight='semibold' className='mb-2'>
									Next Survey Date (Optional)
								</Text>
								<DatePicker
									placeholder='Select date'
									value={
										value
											? value instanceof Date
												? value.toISOString().split('T')[0]
												: new Date(value).toISOString().split('T')[0]
											: ''
									}
									onChange={(newValue) => {
										onChange(newValue ? new Date(newValue) : null)
									}}
								/>
							</div>
						)}
					/>
					<Text variant='xsmall' className='text-gray-600 mt-2'>
						Override the automatic schedule for the next survey
					</Text>
				</div>

				{enpsConfig?.lastExecutionDate && (
					<div className='md:col-span-2'>
						<Text variant='small' weight='semibold'>
							Last Survey Sent
						</Text>
						<Text variant='xsmall' className='text-gray-600 mt-1'>
							{new Date(enpsConfig.lastExecutionDate).toLocaleString()}
						</Text>
					</div>
				)}
			</div>
			<ActionBar
				rightActions={[
					{
						variant: 'secondary' as const,
						label: 'Cancel',
						onClick: () => navigate('/enps'),
					},
					{
						variant: 'primary' as const,
						label: 'Save',
						onClick: handleSubmit(handleSubmitInternal),
					},
				]}
			/>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</form>
	)
}
